const normalizeBaseUrl = (value: string): string => value.trim().replace(/\/+$/, '');

// Default platform URL. Override at build time via PLATFORM_BASE_URL env var or .env file.
const DEFAULT_PLATFORM_BASE_URL = 'https://bomate.cn';
export const PLATFORM_BASE_URL = normalizeBaseUrl(
  import.meta.env.PLATFORM_BASE_URL || DEFAULT_PLATFORM_BASE_URL
);

const PLATFORM_URL_KEY = 'platform_base_url';

export const getPlatformBaseUrl = (): string => {
  return localStorage.getItem(PLATFORM_URL_KEY) || PLATFORM_BASE_URL;
};

export const setPlatformBaseUrl = (url: string): void => {
  localStorage.setItem(PLATFORM_URL_KEY, normalizeBaseUrl(url));
};

export const hasPlatformBaseUrl = (): boolean => {
  return !!localStorage.getItem(PLATFORM_URL_KEY);
};

// ---- 平台可达性状态（Platform reachability）----
export type PlatformStatus = 'online' | 'offline';

type StatusListener = (status: PlatformStatus) => void;
const statusListeners = new Set<StatusListener>();
let currentStatus: PlatformStatus = 'online';
let consecutiveFailures = 0;
const OFFLINE_THRESHOLD = 2;
let recoveryProbeHandle: ReturnType<typeof setTimeout> | null = null;
const RECOVERY_PROBE_INTERVAL_MS = 15_000;

export const getPlatformStatus = (): PlatformStatus => currentStatus;

export const subscribePlatformStatus = (listener: StatusListener): (() => void) => {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
};

const setPlatformStatus = (next: PlatformStatus): void => {
  if (currentStatus === next) return;
  currentStatus = next;
  statusListeners.forEach((fn) => {
    try {
      fn(next);
    } catch (err) {
      console.warn('[platformStatus] listener error:', err);
    }
  });
};

async function probePlatform(): Promise<boolean> {
  try {
    const resp = await fetch('/api/platform/health', {
      method: 'GET',
      signal: AbortSignal.timeout(8000),
    });
    return resp.ok;
  } catch {
    return false;
  }
}

function scheduleRecoveryProbe(): void {
  if (recoveryProbeHandle) return;
  recoveryProbeHandle = setTimeout(async () => {
    recoveryProbeHandle = null;
    if (currentStatus === 'online') return;
    const ok = await probePlatform();
    if (ok) {
      consecutiveFailures = 0;
      setPlatformStatus('online');
    } else {
      scheduleRecoveryProbe();
    }
  }, RECOVERY_PROBE_INTERVAL_MS);
}

export const retryPlatformNow = async (): Promise<boolean> => {
  const ok = await probePlatform();
  if (ok) {
    consecutiveFailures = 0;
    setPlatformStatus('online');
    if (recoveryProbeHandle) {
      clearTimeout(recoveryProbeHandle);
      recoveryProbeHandle = null;
    }
  }
  return ok;
};

export const onPlatformRequestFailed = (): void => {
  consecutiveFailures += 1;
  if (consecutiveFailures >= OFFLINE_THRESHOLD && currentStatus === 'online') {
    setPlatformStatus('offline');
    scheduleRecoveryProbe();
  }
};

export const onPlatformRequestSucceeded = (): void => {
  consecutiveFailures = 0;
  if (currentStatus === 'offline') {
    setPlatformStatus('online');
    if (recoveryProbeHandle) {
      clearTimeout(recoveryProbeHandle);
      recoveryProbeHandle = null;
    }
  }
};
